<template>
  <ion-page>
    <main-header :back-href="transactionListHref"></main-header>
    <ion-content padding>
      <loading-spinner v-if="loading"></loading-spinner>
      <div v-else>
        <h2>Transaction {{ transaction.id }} Details</h2>
        <ion-grid>
          <ion-row>
            <ion-col size="6">
              <h3>Items</h3>
              <ion-list>
                <ion-item
                  v-for="(lineItem, index) in transaction.items"
                  :key="`item-${index}`"
                  :data-cy="`order-item-${lineItem.id}`"
                >
                  <ion-text slot="start">
                    {{ lineItem.quantity }}
                  </ion-text>
                  <ion-label>
                    {{ lineItem.name }}
                    <span v-if="lineItem.options">
                      - {{ lineItem.options.value.name }}
                    </span>
                    <p v-if="lineItem.instructions">
                      {{ lineItem.instructions }}
                    </p>
                  </ion-label>
                </ion-item>
              </ion-list>
              <h3>Location</h3>
              <ion-list>
                <ion-item>
                  <ion-label>
                    {{ transaction.locationName }}
                  </ion-label>
                  <span v-if="!location">
                    Location no longer exists
                  </span>
                  <span v-else-if="transaction.locationName != location.name">
                    Now: {{ location.name }}
                  </span>
                </ion-item>
                <ion-item>
                  <ion-label v-if="transaction.isDelivery()">
                    Delivery: Section {{ transaction.section }}, Row
                    {{ transaction.row }}, Seat
                    {{ transaction.seat }}
                  </ion-label>
                  <ion-label v-else>
                    Pick-up
                  </ion-label>
                </ion-item>
                <ion-item v-if="transaction.instructions">
                  <ion-label>{{ transaction.instructions }}</ion-label>
                </ion-item>
              </ion-list>
            </ion-col>
            <ion-col size="6">
              <h3>Details</h3>
              <ion-list>
                <ion-item>
                  <ion-label>Status</ion-label>
                  {{ transaction.status | ucfirst }}
                </ion-item>
                <ion-item>
                  <ion-label>Requested</ion-label>
                  {{ transaction.requested }}
                </ion-item>
                <ion-item>
                  <ion-label>Created</ion-label>
                  {{ transaction.created }}
                </ion-item>
                <ion-item>
                  <ion-label>Created By</ion-label>
                  <a
                    href="#"
                    @click.prevent="navigateToUser(transaction.userId)"
                  >
                    {{ transaction.userId }}
                  </a>
                </ion-item>
                <ion-item>
                  <ion-label>Preparing</ion-label>
                  {{ transaction.preparingStartAt }}
                </ion-item>
                <ion-item>
                  <ion-label>Prepared By</ion-label>
                  <a
                    href="#"
                    @click.prevent="navigateToUser(transaction.preparingUserId)"
                  >
                    {{ transaction.preparingUserId }}
                  </a>
                </ion-item>
                <ion-item v-if="transaction.isDelivery()">
                  <ion-label>Delivering</ion-label>
                  {{ transaction.deliveringStartAt }}
                </ion-item>
                <ion-item v-if="transaction.isDelivery()">
                  <ion-label>Delivered By</ion-label>
                  <a
                    href="#"
                    @click.prevent="
                      navigateToUser(transaction.deliveringUserId)
                    "
                  >
                    {{ transaction.deliveringUserId }}
                  </a>
                </ion-item>
                <ion-item v-if="!transaction.isDelivery()">
                  <ion-label>Ready</ion-label>
                  {{ transaction.readyStartAt }}
                </ion-item>
                <ion-item v-if="!transaction.isDelivery()">
                  <ion-label>Marked Ready By</ion-label>
                  <a
                    href="#"
                    @click.prevent="navigateToUser(transaction.readyUserId)"
                  >
                    {{ transaction.readyUserId }}
                  </a>
                </ion-item>
                <ion-item>
                  <ion-label>Completed</ion-label>
                  {{ transaction.completedAt }}
                </ion-item>
                <ion-item>
                  <ion-label>Completed By</ion-label>
                  <a
                    href="#"
                    @click.prevent="navigateToUser(transaction.completedUserId)"
                  >
                    {{ transaction.completedUserId }}
                  </a>
                </ion-item>
                <ion-item>
                  <ion-label>
                    Item Total
                  </ion-label>
                  <span>{{ itemTotal | usd }}</span>
                </ion-item>
                <ion-item>
                  <ion-label>
                    Service Charge
                  </ion-label>
                  <span>{{ transaction.serviceCharge | usd }}</span>
                </ion-item>
                <ion-item>
                  <ion-label>
                    Total
                  </ion-label>
                  <span>{{ transaction.totalPrice | usd }}</span>
                </ion-item>
                <ion-item>
                  <ion-label>
                    Stripe ID
                  </ion-label>
                  <span>{{ transaction.stripePaymentIntentId }}</span>
                </ion-item>
              </ion-list>
            </ion-col>
          </ion-row>
        </ion-grid>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { mapGetters } from "vuex";
import Order from "@/models/Order";
import Location from "@/models/Location";
import errorAlert from "@/ErrorAlert";

export default {
  title: "Transaction Detail",

  data: function() {
    return {
      loading: true,
      transaction: null,
      location: null,
      users: {}
    };
  },

  created() {
    if (!this.userIsAdmin) {
      return this.$router.push("/").catch(e => e);
    }

    this.$firestore()
      .collection("orders")
      .withConverter(Order.firebaseConverter)
      .doc(this.$route.params.id)
      .get()
      .then(snapshot => {
        let transaction = snapshot.data();
        this.transaction = transaction;

        return this.$firestore()
          .collection("locations")
          .withConverter(Location.firebaseConverter)
          .doc(this.transaction.locationId)
          .get()
          .then(locationSnapshot => {
            this.location = locationSnapshot.data();
          });
      })
      .catch(e => {
        errorAlert(e.message);
      })
      .finally(() => {
        this.loading = false;
      });
  },

  computed: {
    ...mapGetters("auth", ["userIsAdmin"]),

    transactionListHref() {
      return this.$router.resolve({
        name: "Web: Transaction List"
      }).href;
    },

    itemTotal() {
      let itemTotal =
        this.transaction.totalPrice - this.transaction.serviceCharge;

      return !isNaN(itemTotal) ? itemTotal : "";
    }
  },

  methods: {
    navigateToUser(uid) {
      this.$router.push({
        name: "Web: User Edit",
        params: { uid }
      });
    }
  }
};
</script>
